import { ArrowLeftIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CalendarHeader } from '~/features/shared/calendar/CalendarHeader';
import { CalendarSelectField } from '~/features/shared/calendar/CalendarSelectField';
import { CALENDAR_OUTLINE_BUTTON } from '~/features/shared/calendar/controlStyles';
import { formatDisplayDate, toISODate } from '~/features/shared/calendar/dateUtils';
import { paths } from '~/routing';
import { buildContractUrl } from '~/routing/urls';
import { AvailabilityCalendarGrid } from './components/AvailabilityCalendarGrid';
import { AvailabilityEditorPanel } from './components/AvailabilityEditorPanel';
import { AvailabilityLegend } from './components/AvailabilityLegend';
import { BulkDateToolbar } from './components/BulkDateToolbar';
import { CalendarContractList } from './components/CalendarContractList';
import { RecurringAvailabilityDialog } from './components/RecurringAvailabilityDialog';
import { contractsByStartDate, contractsCoveringDates, contractsForScope } from './contractMarkers';
import { availabilityScopeFromParam, availabilityScopeToParam } from './types';
import { useAvailabilityCalendarService } from './useAvailabilityCalendarService';

// Headless UI (the scope select's portaled menu, the Recurring availability
// dialog) and react-select (the scope select itself) all render outside this
// component's subtree, so containment checks can't see them -- these roles
// are how they're identified instead.
const OPEN_OVERLAY_SELECTOR =
  '[role="dialog"], [role="listbox"], [role="menu"], [role="option"], [data-headlessui-portal]';

const CLICK_OFF_EXCLUDED_SELECTOR = [
  '[data-calendar-date]',
  '[data-cy="provider-calendar-bulk-toolbar"]',
  '[data-cy="provider-calendar-header"]',
  '[data-cy="provider-calendar-scope-bar"]',
  '[data-cy="provider-calendar-selected-contracts"]',
  OPEN_OVERLAY_SELECTOR,
].join(', ');

/** Manage calendar availability while retaining the listing scope for return navigation. */
export const ProviderAvailabilityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scopeParam = searchParams.get('serviceListingId');
  const initialScope = useMemo(() => availabilityScopeFromParam(scopeParam), [scopeParam]);
  const calendar = useAvailabilityCalendarService(initialScope);
  // [MCV-1821] Contract starts shown for the current scope (JOB in every scope, SERVICE in All
  // and in its own listing's).
  const scopedContracts = useMemo(
    () => contractsForScope(calendar.contracts, calendar.scope),
    [calendar.contracts, calendar.scope]
  );
  const contractsByDate = useMemo(() => contractsByStartDate(scopedContracts), [scopedContracts]);
  // [MCV-1844] Every contract covering a selected date, whatever its status. The editor lists the
  // ones covering the date(s) it is open on.
  const selectedContracts = useMemo(
    () => contractsCoveringDates(scopedContracts, calendar.selectedDates),
    [scopedContracts, calendar.selectedDates]
  );
  const editorContracts = useMemo(
    () => contractsCoveringDates(scopedContracts, calendar.editorDates),
    [scopedContracts, calendar.editorDates]
  );
  const viewContract = (contractId: number) => navigate(buildContractUrl(contractId));
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const focusReturnDate = useRef<string | null>(null);
  // Finding 2: selection now survives navigation, so the editor can close on
  // a date that isn't rendered (navigated away while it was open). There is
  // then no `[data-calendar-date]` button to focus, so this records whatever
  // had focus when the editor opened -- the Edit/View availability button,
  // in every real flow -- as a fallback. Cleared after use so a stale opener
  // is never focused by a later, unrelated close.
  const editorOpener = useRef<HTMLElement | null>(null);
  // Selecting a date needs a *settled* authoritative projection. Without a load
  // the days are an empty projection, not an empty calendar, so mutating would
  // delete hidden rows; and `hasAuthoritativeLoad` stays true across a reload
  // while ON_TOGGLE_DATE is only handled in `ready`, so dropping `busy` here
  // leaves the days clickable with every click silently discarded.
  const selectionDisabled = calendar.busy || !calendar.hasAuthoritativeLoad;
  // Every editor gets this too, not just the bulk toolbar: the machine guards
  // stop the request either way, but a live-looking Save that silently
  // discards the user's work is worse than one that is visibly unavailable.
  const editingDisabled = selectionDisabled || !calendar.timezone;
  // The editor and the recurrence dialog derive read-only from `scope` on their
  // own, so only the controls without that notion need the scope term here.
  const mutationDisabled = editingDisabled || calendar.scope.kind === 'all';
  const visibleRules = useMemo(
    () =>
      calendar.rules.filter((rule) => {
        if (calendar.scope.kind === 'all') return true;
        if (calendar.scope.kind === 'general') return rule.serviceListingId === null;
        return rule.serviceListingId === calendar.scope.listingId;
      }),
    [calendar.rules, calendar.scope]
  );

  // Selection now survives month/week navigation (MCV-1674), so some of it
  // can be off-screen. "Clear editable" deletes every selected date,
  // including those -- the count must make that visible.
  const visibleDateSet = useMemo(
    () => new Set(calendar.visibleDates.map(toISODate)),
    [calendar.visibleDates]
  );
  const offScreenSelectedCount = useMemo(
    () => calendar.selectedDates.filter((date) => !visibleDateSet.has(date)).length,
    [calendar.selectedDates, visibleDateSet]
  );

  // C2 regression (Finding 1): the read-only 'all' scope has no template to
  // save, so View availability must only ever open for exactly one selected
  // date that is actually on screen -- the same date the machine's guard
  // requires be inside the loaded range. Two-plus dates, or a lone date that
  // scrolled off-screen, would otherwise open a blank draft over a real
  // saved entry with Save permanently disabled.
  const canOpenReadOnlyEditor =
    calendar.selectedDates.length === 1 && visibleDateSet.has(calendar.selectedDates[0]);

  const changeScope = (value: string) => {
    const nextScope = availabilityScopeFromParam(value);
    const nextParams = new URLSearchParams(searchParams);
    const nextScopeParam = availabilityScopeToParam(nextScope);
    if (nextScopeParam === null) nextParams.delete('serviceListingId');
    else nextParams.set('serviceListingId', nextScopeParam);
    setSearchParams(nextParams, { replace: true });
    calendar.setScope(nextScope);
  };

  const openEditor = () => {
    // Snapshot whatever has focus right now -- the Edit/View availability
    // button that was just clicked or activated via keyboard -- so close can
    // fall back to it when the edited date's cell isn't rendered.
    editorOpener.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    calendar.openEditor();
  };

  const closeEditor = () => {
    // Multi-date edits return focus to the first date in the set -- there is
    // no single cell that uniquely represents the whole edit.
    focusReturnDate.current = calendar.editorDates[0] ?? null;
    calendar.closeEditor();
  };

  useEffect(() => {
    if (calendar.editorDates.length > 0) return;
    if (!focusReturnDate.current && !editorOpener.current) return;
    const date = focusReturnDate.current;
    const opener = editorOpener.current;
    focusReturnDate.current = null;
    editorOpener.current = null;
    const dateButton = date
      ? document.querySelector<HTMLButtonElement>(`button[data-calendar-date="${date}"]`)
      : null;
    // Prefer the date cell when it's actually rendered; otherwise fall back
    // to the control that opened the editor so keyboard focus is never
    // simply dropped.
    if (dateButton) dateButton.focus();
    else opener?.focus();
  }, [calendar.editorDates]);

  // MCV-1691: click off the calendar to start a fresh selection. Everything
  // the user can legitimately interact with while selecting is excluded --
  // day cells, the toolbar (Mark available/unavailable/Clear editable/Edit
  // all live there), the header's navigation (or Next/Previous would undo
  // MCV-1674's "selection survives navigation"), the scope picker's trigger,
  // and any open portal/overlay (the scope menu and the recurring dialog are
  // both rendered via a portal, detached from this subtree). While the editor
  // is open a stray click must never drop an unsaved draft, so this does
  // nothing at all in that case -- Escape and the panel's own close button
  // are still available.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (calendar.editorDates.length > 0) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(CLICK_OFF_EXCLUDED_SELECTOR)) return;
      calendar.clearSelection();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [calendar.editorDates, calendar.clearSelection]);

  // MCV-1691: Escape clears the selection, but only when the editor is
  // closed -- SlideInPanel's own (non-modal) Escape handler owns closing the
  // editor, and one press must do only one thing. `calendar.editorDates`
  // is checked directly rather than relying purely on `defaultPrevented`
  // (SlideInPanel does call preventDefault(), but React runs child effects
  // before parent effects, so this also holds regardless of listener order).
  // An open portal/overlay (scope menu, recurring dialog) owns its own
  // Escape too, so this backs off there as well.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (calendar.editorDates.length > 0) return;
      if (document.querySelector(OPEN_OVERLAY_SELECTOR)) return;
      calendar.clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [calendar.editorDates, calendar.clearSelection]);

  return (
    <div className="flex flex-col gap-4 pb-6" data-cy="provider-availability-calendar">
      <Link
        className={`${CALENDAR_OUTLINE_BUTTON} self-start bg-white`}
        to={
          calendar.scope.kind === 'listing'
            ? `${paths.hlx.myServices}?serviceListingId=${calendar.scope.listingId}`
            : paths.hlx.myServices
        }
      >
        <ArrowLeftIcon aria-hidden="true" className="size-4 shrink-0" />
        Back to My Services
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-left">
          <h1 className="sub-heading-sb text-grey-100">Availability Calendar</h1>
          <p className="mt-1 body-text text-grey-500">
            Select dates to update availability or add shift and call details.
          </p>
        </div>
      </header>

      <div
        className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-grey-900 bg-white p-3"
        data-cy="provider-calendar-scope-bar"
      >
        <div className="flex w-full min-w-0 flex-col items-start gap-2 text-left body-text-sb text-grey-400 sm:w-auto sm:flex-row sm:items-center">
          Availability scope
          <CalendarSelectField
            ariaLabel="Availability scope"
            cyTag="provider-calendar-scope-filter"
            className="w-full min-w-0 sm:w-56"
            disabled={calendar.busy}
            onChange={changeScope}
            value={
              calendar.scope.kind === 'listing'
                ? String(calendar.scope.listingId)
                : calendar.scope.kind
            }
            options={[
              { value: 'all', label: 'All availability' },
              { value: 'general', label: 'General availability' },
              ...calendar.listings.map((listing) => ({
                value: String(listing.id),
                label: listing.listingTitle,
              })),
            ]}
          />
        </div>
        <AvailabilityLegend />
      </div>

      {calendar.scope.kind === 'all' ? (
        <p
          className="rounded-md bg-grey-950 p-3 text-sm text-grey-500"
          data-cy="provider-calendar-all-scope-notice"
          role="status"
        >
          All availability combines General and service-listing calendars as a read-only overview.
          Choose a specific scope to make changes.
        </p>
      ) : null}

      {/* data-cy wrapper only: excludes header navigation from the
          click-off handler below without touching the shared component. */}
      <div data-cy="provider-calendar-header">
        <CalendarHeader
          anchor={calendar.anchor}
          disabled={calendar.busy}
          onAnchorChange={calendar.setAnchor}
          onViewModeChange={calendar.setViewMode}
          today={new Date(`${calendar.todayISO}T12:00:00`)}
          viewMode={calendar.viewMode}
        />
      </div>

      {!calendar.timezone ? (
        <div
          className="rounded-md border border-error/20 bg-white p-3 text-sm text-error"
          role="alert"
        >
          Your browser timezone could not be determined. Availability editing is disabled.
        </div>
      ) : null}

      {calendar.error ? (
        <div
          className="flex items-center justify-between gap-3 rounded-md border border-error/20 bg-white p-3"
          role="alert"
        >
          <span className="text-sm text-error">{calendar.error}</span>
          <button className={CALENDAR_OUTLINE_BUTTON} onClick={calendar.retry} type="button">
            Retry
          </button>
        </div>
      ) : null}
      {calendar.conflict ? (
        <div
          className="rounded-md border border-warning/20 bg-warning-50 p-3 text-sm text-warning-950"
          role="alert"
        >
          {calendar.conflict.message} Locked entries:{' '}
          {calendar.conflict.entryIds.join(', ') || 'unknown'}.
        </div>
      ) : null}
      {calendar.notice ? (
        <div aria-live="polite" className="rounded-md bg-success-subtle p-3 text-sm text-good">
          {calendar.notice}
        </div>
      ) : null}
      {calendar.contractsError ? (
        <p
          className="rounded-md bg-grey-950 p-3 text-sm text-grey-500"
          data-cy="provider-calendar-contracts-error"
          role="status"
        >
          Your contracts could not be loaded, so their start dates are not shown. Availability is
          unaffected.
        </p>
      ) : null}
      {calendar.loading ? (
        <p aria-live="polite" className="body-text text-grey-500">
          Loading availability…
        </p>
      ) : null}

      <BulkDateToolbar
        busy={mutationDisabled}
        count={calendar.selectedDates.length}
        offScreenCount={offScreenSelectedCount}
        onOpenEditor={openEditor}
        editorDisabled={
          calendar.scope.kind === 'all'
            ? selectionDisabled || !canOpenReadOnlyEditor
            : editingDisabled
        }
        readOnly={calendar.scope.kind === 'all'}
        onClear={calendar.clearDates}
        onClearSelection={calendar.clearSelection}
        onMarkAvailable={() => calendar.markDates('AVAILABLE')}
        onMarkUnavailable={() => calendar.markDates('UNAVAILABLE')}
        onOpenRecurring={() => setRecurrenceOpen(true)}
        recurringDisabled={editingDisabled}
      />

      {calendar.editorDates.length === 0 && selectedContracts.length > 0 ? (
        <section
          aria-label="Contracts on the selected dates"
          className="rounded-lg border border-grey-900 bg-white p-3"
          data-cy="provider-calendar-selected-contracts"
        >
          <h2 className="mb-2 text-left body-text-sb text-grey-100">
            {calendar.selectedDates.length === 1
              ? `Contracts on ${formatDisplayDate(calendar.selectedDates[0])}`
              : 'Contracts on the selected dates'}
          </h2>
          <CalendarContractList
            contracts={selectedContracts}
            cyPrefix="provider-calendar-selected-contract"
            onViewContract={viewContract}
          />
        </section>
      ) : null}

      <AvailabilityCalendarGrid
        anchor={calendar.anchor}
        contractsByDate={contractsByDate}
        daysByDate={calendar.daysByDate}
        disabled={selectionDisabled}
        onDateClick={calendar.toggleDate}
        selectedDates={calendar.selectedDates}
        todayISO={calendar.todayISO}
        viewMode={calendar.viewMode}
        visibleDates={calendar.visibleDates}
      />

      {!calendar.loading && calendar.hasAuthoritativeLoad && calendar.daysByDate.size === 0 ? (
        <div className="rounded-xl border border-grey-900 bg-white p-8 text-center">
          <CalendarDaysIcon className="mx-auto size-10 text-grey-700" />
          <p className="mt-3 body-text-sb text-grey-100">No availability in this range</p>
          <p className="mt-1 body-text text-grey-500">Select future dates to get started.</p>
        </div>
      ) : null}

      <AvailabilityEditorPanel
        busy={editingDisabled}
        conflict={calendar.conflict}
        contracts={editorContracts}
        dates={calendar.editorDates}
        day={calendar.editorDay}
        editorMode={calendar.isSingleDateEditor ? 'single' : 'template'}
        mutationError={calendar.error}
        onClear={() =>
          calendar.editorDates.length === 1 && calendar.clearDay(calendar.editorDates[0])
        }
        onClose={closeEditor}
        onSave={(entries) =>
          calendar.editorDates.length === 1 && calendar.saveDay(calendar.editorDates[0], entries)
        }
        onSaveDays={(days) => calendar.saveDays(days)}
        onViewContract={viewContract}
        scope={calendar.scope}
        timezone={calendar.timezone}
      />

      <RecurringAvailabilityDialog
        busy={editingDisabled}
        conflict={calendar.conflict}
        mutationError={calendar.error}
        notice={calendar.notice}
        onClose={() => setRecurrenceOpen(false)}
        onDelete={calendar.deleteRule}
        onSaveException={calendar.saveException}
        onSave={calendar.saveRule}
        open={recurrenceOpen}
        readOnly={calendar.scope.kind === 'all'}
        rules={visibleRules}
        scope={calendar.scope}
        timezone={calendar.timezone}
        todayISO={calendar.todayISO}
      />
    </div>
  );
};
